// File: packages/components/src/homepage/footer-cta/FooterCTASection.tsx
import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Button } from '@dopomogai/ui';
import { HeroBackground } from '../hero/HeroBackground';

type Accent =
  | 'emerald' | 'violet' | 'blue' | 'cyan' | 'amber' | 'rose'
  | 'indigo' | 'purple' | 'teal' | 'orange' | 'lime' | 'fuchsia'
  | 'pink' | 'slate' | 'gray';

type Cta = {
  label?: string;
  labelKey?: string;
  actionId: string;
  variant?: 'primary' | 'secondary' | 'outline';
  payload?: any;
};

export type FooterCTASectionProps = {
  title?: string;
  titleKey?: string;
  subtitle?: string;
  subtitleKey?: string;
  ctas: Cta[];
  onAction?: (actionId: string, payload?: any) => void;
  className?: string;

  // Visuals
  showBackground?: boolean;
  backgroundVariant?: 'grid-blobs' | 'neo-grid';
  backgroundIntensity?: 'subtle' | 'bold';
  accent?: Accent;
  showTopStripe?: boolean;
  chips?: string[];
};

const stripeColor = (accent?: Accent) => {
  switch (accent) {
    case 'violet': return 'bg-violet-400';
    case 'blue': return 'bg-blue-400';
    case 'cyan': return 'bg-cyan-400';
    case 'amber': return 'bg-amber-400';
    case 'rose': return 'bg-rose-400';
    case 'indigo': return 'bg-indigo-400';
    case 'purple': return 'bg-purple-400';
    case 'teal': return 'bg-teal-400';
    case 'orange': return 'bg-orange-400';
    case 'lime': return 'bg-lime-400';
    case 'fuchsia': return 'bg-fuchsia-400';
    case 'pink': return 'bg-pink-400';
    case 'slate': return 'bg-slate-400';
    case 'gray': return 'bg-gray-400';
    default: return 'bg-emerald-400';
  }
};

export const FooterCTASection: React.FC<FooterCTASectionProps> = ({
  title,
  titleKey,
  subtitle,
  subtitleKey,
  ctas,
  onAction,
  className,
  showBackground = true,
  backgroundVariant,
  backgroundIntensity,
  accent = 'emerald',
  showTopStripe = true,
  chips = [],
}) => {
  const { t } = useTranslation('homepage-2');
  const resolvedTitle = titleKey ? t(titleKey) : title;
  const resolvedSubtitle = subtitleKey ? t(subtitleKey) : subtitle;

  return (
    <section className={clsx('bg-slate-50', className)}>
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 py-14 md:py-20">
        <div
          className={clsx(
            // UPDATED: Changed bg-white to bg-slate-900 (Dark Mode)
            'relative overflow-hidden border-2 border-black rounded-3xl bg-slate-900',
            'px-6 md:px-10 py-10 md:py-14',
            'transition-all duration-200 hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[4px_4px_0_0_rgb(17,24,39)]'
          )}
        >
          {showBackground && (
            // UPDATED: Removed opacity-50 to allow full dark blue & crisp grid
            <HeroBackground /> 
          )}
          
          {showTopStripe && (
            <div aria-hidden="true" className={clsx('absolute inset-x-2 top-2 h-2 rounded-full z-20', stripeColor(accent))} />
          )}

          <div className="relative text-center z-10">
            {resolvedTitle ? (
              // UPDATED: Text Color to White
              <h3 className="text-3xl md:text-5xl font-black text-white tracking-tight leading-none mb-4">
                {resolvedTitle}
              </h3>
            ) : null}
            
            {resolvedSubtitle ? (
              // UPDATED: Text Color to Slate-300
              <p className="mt-4 text-slate-300 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed font-medium">
                {resolvedSubtitle}
              </p>
            ) : null}

            {chips.length > 0 && (
              <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
                {chips.map((c, i) => (
                  <span
                    key={i}
                    // UPDATED: Chips are now White BG with Dark Text for contrast against dark card
                    className="inline-flex items-center rounded-lg border-2 border-transparent bg-white px-3 py-1 text-xs font-bold uppercase tracking-wide text-slate-900 shadow-sm"
                  >
                    {c}
                  </span>
                ))}
              </div>
            )}

            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              {ctas.map((c, i) => (
                <Button
                  key={i}
                  size="xl"
                  variant={c.variant || (i === 0 ? 'primary' : 'outline')}
                  text={c.labelKey ? t(c.labelKey) : (c.label ?? '')}
                  onClick={() => onAction?.(c.actionId, c.payload)}
                  // UPDATED: Ensure outline/ghost buttons are visible on dark background
                  className={c.variant === 'primary' 
                    ? 'shadow-xl' 
                    : 'bg-transparent text-white border-2 border-slate-600 hover:bg-white hover:text-slate-900 hover:border-white'
                  }
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FooterCTASection;